import { parseMenuConfig, type MenuItem } from '../utils/menu';
import type { ModuleManifest, ModuleId } from './types';
import { findOwningModule } from './paths';
import { enabledModules, enabledModuleIds } from './registry';
import { toServerId } from '../../shared/module-flags';

/** PURE — keep items owned by no module (WP slugs, hash, external) or by an
 *  enabled module; drop the rest with a dev warning. */
export const filterMenuItems = (
  items: MenuItem[],
  enabledIds: ModuleId[],
  warn: (msg: string) => void = (msg) => {
    if (import.meta.env.DEV) console.warn(msg);
  }
): MenuItem[] =>
  items.filter((item) => {
    const owner = findOwningModule(item.path);
    if (owner === null || enabledIds.includes(owner)) return true;
    warn(
      `[modules] Menu item "${item.title}" (${item.path}) dropped: ` +
        `module "${owner}" is not installed (VITE_MODULE_${owner.toUpperCase()}).`
    );
    return false;
  });

/** PURE — default nav from manifests, in registry order. */
export const defaultMenuItems = (modules: ModuleManifest[]): MenuItem[] =>
  modules.flatMap((m) => m.menuItems ?? []);

/** PURE — drop items whose owning module is runtime-disabled. The map arrives
 *  in SERVER shape (config/public modules use underscore ids); owners are
 *  frontend ModuleIds (hyphen) — map at this boundary (PRP-10 D6). */
export const filterByRuntimeModules = (
  items: MenuItem[],
  runtimeModules?: Record<string, boolean>
): MenuItem[] =>
  runtimeModules
    ? items.filter((item) => {
        const owner = findOwningModule(item.path);
        return owner === null || runtimeModules[toServerId(owner)] !== false;
      })
    : items;

export interface RuntimeMenuItem extends MenuItem {
  enabled?: boolean;
}

/** THE menu source for Header/Footer.
 *  runtimeMenu present (config/public menu, PRP-10) → admin-managed items
 *    (enabled only) ∩ installed modules ∩ runtime-enabled modules.
 *  otherwise: VITE_MENU_CONFIG present → configured items ∩ enabled modules;
 *  VITE_MENU_CONFIG empty → enabled manifests' default menuItems.
 *  undefined/null runtimeMenu (config loading, static fork) MUST render the
 *  env fallback immediately — never an empty-menu flash. */
export const getMenuItems = (
  runtimeModules?: Record<string, boolean>,
  runtimeMenu?: RuntimeMenuItem[] | null
): MenuItem[] => {
  if (runtimeMenu && runtimeMenu.length > 0) {
    const active = runtimeMenu
      .filter((item) => item.enabled !== false)
      .map(({ title, path }) => ({ title, path, external: path.startsWith('http') }));
    return filterByRuntimeModules(filterMenuItems(active, enabledModuleIds), runtimeModules);
  }
  const configured = parseMenuConfig(); // existing util, unchanged
  if (configured.length === 0)
    return filterByRuntimeModules(defaultMenuItems(enabledModules), runtimeModules);
  return filterByRuntimeModules(filterMenuItems(configured, enabledModuleIds), runtimeModules);
};
